/**
 * Tri de tableau, uniforme sur tous les écrans qui en ont besoin (Parc GRV, Mouvements, Maintenance,
 * Documents, Fiche de vie) — un même comportement une fois appris doit fonctionner partout ailleurs.
 *
 * Premier clic sur une colonne : tri croissant. Un second clic sur la même colonne l'inverse. Cliquer
 * une autre colonne repart en croissant sur celle-ci, jamais en conservant le sens de la précédente.
 */
import { useMemo, useState } from "react";

export type Tri<C extends string> = { colonne: C; descendant: boolean };

/**
 * `triInitial` ne fixe que ce qui est affiché avant toute interaction (ex. positions et mouvements,
 * les plus récents en tête) — cliquer sur un en-tête suit toujours la même règle, quel que soit ce
 * point de départ : croissant au premier clic sur cette colonne, décroissant au second.
 */
export function useTriTableau<C extends string>(triInitial: C | Tri<C>) {
  const [tri, definirTri] = useState<Tri<C>>(
    typeof triInitial === "string" ? { colonne: triInitial, descendant: false } : triInitial,
  );

  function basculerTri(colonne: C) {
    definirTri((precedent) =>
      precedent.colonne === colonne
        ? { colonne, descendant: !precedent.descendant }
        : { colonne, descendant: false },
    );
  }

  return { tri, basculerTri, definirTri };
}

/** Trie une liste selon `tri`, `valeurDe` extrayant la valeur comparable pour une colonne donnée. */
export function useTrie<T, C extends string>(
  items: T[],
  tri: Tri<C>,
  valeurDe: (item: T, colonne: C) => string | number | null | undefined,
): T[] {
  return useMemo(() => {
    const copie = [...items];
    copie.sort((a, b) => {
      const va = valeurDe(a, tri.colonne);
      const vb = valeurDe(b, tri.colonne);
      let comparaison: number;
      if (typeof va === "number" && typeof vb === "number") {
        comparaison = va - vb;
      } else {
        comparaison = String(va ?? "").localeCompare(String(vb ?? ""));
      }
      return tri.descendant ? -comparaison : comparaison;
    });
    return copie;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, tri.colonne, tri.descendant]);
}

export function EnTeteTriable<C extends string>({
  colonne,
  libelle,
  tri,
  basculerTri,
}: {
  colonne: C;
  libelle: string;
  tri: Tri<C>;
  basculerTri: (colonne: C) => void;
}) {
  const actif = tri.colonne === colonne;
  return (
    <th scope="col">
      <button
        type="button"
        className="tableau__tri"
        onClick={() => basculerTri(colonne)}
        aria-sort={actif ? (tri.descendant ? "descending" : "ascending") : "none"}
      >
        {libelle} {actif ? (tri.descendant ? "▼" : "▲") : ""}
      </button>
    </th>
  );
}
